import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Button,
    Box,
    Checkbox,
    Chip,
    Divider,
    Drawer,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { alpha } from '@mui/material/styles';
import { Virtuoso } from 'react-virtuoso';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion, useSpring } from 'framer-motion';
import { usePelicanMonitor } from './usePelicanMonitor';
import { useBankniftyMonitor } from './useBankniftyMonitor';
import { useCobraMonitor } from './useCobraMonitor';
import { useTelemetryCatalog } from './useTelemetryCatalog';
import { getCurrentTradingDayStart } from './timeUtils';
import resolveApiBase from './apiBase';
import GlassPaper from '../../components/GlassPaper';
import { DEFAULT_SEVERITIES, useTradingCockpitStore } from '../../state/tradingCockpitStore';

const ALL_ACCOUNTS = 'all-accounts';
const SNAPSHOT_QUERY_KEY = ['trading-cockpit', 'snapshots'];
const SNAPSHOT_ENDPOINT = '/api/trading-cockpit/snapshot';
const ACTIVITY_ENDPOINT = '/api/trading-cockpit/activities';
const ACTIVITY_ACTION_ENDPOINT = '/api/trading-cockpit/activities/actions';
const PRE_TRADE_ACTIONS = [
    { id: 'broker-login', label: 'Broker Login' },
    { id: 'instrument-sync', label: 'Instrument Sync' },
    { id: 'sanity-checks', label: 'Sanity Checks' },
];
const POST_TRADE_ACTIONS = [
    { id: 'download-fills', label: 'Download Fills' },
    { id: 'archive-reports', label: 'Archive Reports' },
    { id: 'reconcile-positions', label: 'Reconcile Positions' },
];
const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;

const formatCount = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }
    return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return '—';
    }
    const formatted = Math.abs(number).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    return number < 0 ? `₹ -${formatted}` : `₹ ${formatted}`;
};

const formatRelativeTime = (value) => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSeconds < 60) {
        return `${diffSeconds}s ago`;
    }
    if (diffSeconds < 3600) {
        return `${Math.floor(diffSeconds / 60)}m ago`;
    }
    if (diffSeconds < 86400) {
        return `${Math.floor(diffSeconds / 3600)}h ago`;
    }
    return `${Math.floor(diffSeconds / 86400)}d ago`;
};

const normaliseAccountKey = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim().toLowerCase();
};

const normaliseStrategyKey = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim().toLowerCase();
};

const mergeStrategyEntries = (primary = [], secondary = []) => {
    const merged = new Map();
    const addEntry = (entry) => {
        if (!entry) {
            return;
        }
        const fallbackId = entry.strategyId || entry.displayName || entry.activityType || `strategy-${merged.size}`;
        const key = normaliseStrategyKey(fallbackId) || `strategy-${merged.size}`;
        if (merged.has(key)) {
            const existing = merged.get(key);
            merged.set(key, {
                ...existing,
                ...entry,
                telemetry: entry.telemetry || existing.telemetry,
                summary: entry.summary || existing.summary,
                layers: entry.layers?.length ? entry.layers : existing.layers,
                events: entry.events?.length ? entry.events : existing.events,
            });
        } else {
            merged.set(key, { ...entry });
        }
    };
    primary.forEach(addEntry);
    secondary.forEach(addEntry);
    return Array.from(merged.values());
};

const resolveApiUrl = (path) => {
    if (!API_BASE) {
        return path;
    }
    const normalisedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${normalisedBase}${path.startsWith('/') ? path : `/${path}`}`;
};

const pnlTone = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'neutral';
    }
    if (Number(value) > 0) {
        return 'positive';
    }
    if (Number(value) < 0) {
        return 'negative';
    }
    return 'neutral';
};

const statusTone = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'connected':
        case 'active':
        case 'operational':
        case 'success':
        case 'live':
            return 'positive';
        case 'error':
        case 'halted':
        case 'failed':
        case 'offline':
            return 'negative';
        case 'pending':
        case 'armed':
        case 'degraded':
        case 'warning':
        case 'initialising':
        case 'initializing':
            return 'warning';
        case 'simulation':
        case 'idle':
        case 'paused':
        default:
            return 'neutral';
    }
};

const deriveAccountStatus = (strategies) => {
    if (!strategies?.length) {
        return 'idle';
    }
    const statuses = strategies.map((strategy) => (strategy.status || '').toLowerCase());
    if (statuses.some((status) => ['error', 'halted', 'failed'].includes(status))) {
        return 'error';
    }
    if (statuses.some((status) => ['warning', 'degraded'].includes(status))) {
        return 'warning';
    }
    if (statuses.some((status) => ['active', 'connected', 'operational', 'live'].includes(status))) {
        return 'active';
    }
    return statuses[0] || 'idle';
};

const parseDateSafe = (value) => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const latestTelemetryTimestamp = (strategy) => {
    if (!strategy) {
        return null;
    }
    const summaryDate = parseDateSafe(strategy.summary?.lastHeartbeatAt);
    const layerDates = (strategy.layers || [])
        .map((layer) => parseDateSafe(layer.capturedAt))
        .filter(Boolean);
    const eventDates = (strategy.events || [])
        .map((event) => parseDateSafe(event.occurredAt))
        .filter(Boolean);

    return [...layerDates, summaryDate, ...eventDates].reduce((latest, current) => {
        if (!current) {
            return latest;
        }
        if (!latest || current > latest) {
            return current;
        }
        return latest;
    }, null);
};

const getStaleBoundary = () => getCurrentTradingDayStart();

const resetStrategyTelemetry = (strategy) => {
    const latest = latestTelemetryTimestamp(strategy);
    if (!latest) {
        return strategy;
    }
    const boundary = getStaleBoundary();
    if (latest >= boundary) {
        return strategy;
    }

    return {
        ...strategy,
        status: 'initialising',
        netPnl: 0,
        layers: [],
        events: [],
        summary: {
            ...(strategy.summary || {}),
            status: 'initialising',
            netPnl: 0,
            liveLayers: 0,
            simulationLayers: 0,
            lastHeartbeatAt: null,
            lastMessage: 'Waiting for new session telemetry…',
        },
    };
};

const normalizeStrategy = (strategy = {}, fallback = {}) => {
    const summary = {
        ...(fallback.summary || {}),
        ...(strategy.summary || {}),
    };
    const layers = Array.isArray(strategy.layers)
        ? strategy.layers
        : Array.isArray(fallback.layers)
        ? fallback.layers
        : [];
    const events = Array.isArray(strategy.events)
        ? strategy.events
        : Array.isArray(fallback.events)
        ? fallback.events
        : [];

    const strategyId =
        strategy.strategyId ||
        strategy.id ||
        fallback.strategyId ||
        fallback.id ||
        `strategy-${Math.random().toString(16).slice(2)}`;

    const normalisedEvents = events.map((event, index) => ({
        ...event,
        strategyId: event?.strategyId || strategyId,
        id: event?.id || `${strategyId}-event-${index}`,
    }));

    const netPnl = strategy.netPnl ?? summary.netPnl ?? fallback.netPnl ?? 0;
    const status =
        strategy.status ||
        summary.status ||
        fallback.status ||
        (layers.some((layer) => layer.status === 'active') ? 'active' : 'idle');

    return {
        strategyId,
        name: strategy.name || strategy.label || fallback.name || fallback.label || 'Strategy',
        status,
        netPnl,
        summary: {
            ...summary,
            netPnl,
            status,
        },
        layers,
        events: normalisedEvents,
    };
};

const composeMonitorAccounts = (state, defaults = {}) => {
    if (!state) {
        return [];
    }

    const {
        strategyId: defaultStrategyId = 'strategy',
        strategyLabel: defaultStrategyLabel = 'Strategy',
        accountPrefix = `${defaultStrategyId}-account`,
    } = defaults;

    const incomingAccounts = Array.isArray(state.accounts)
        ? state.accounts.filter((account) => account && (account.accountId || account.id))
        : [];

    if (incomingAccounts.length) {
        return incomingAccounts.map((account, index) => {
            const accountId = account.accountId || account.id || `${accountPrefix}-${index}`;
            const aliasCandidate =
                account.accountAlias || account.alias || account.accountName || account.name || account.label || null;
            const accountName =
                (aliasCandidate || account.accountName || account.name || account.label || `Account ${index + 1}`).trim();
            const accountAlias = aliasCandidate ? String(aliasCandidate).trim() : accountName;
            const clientCandidate =
                account.client ||
                account.clientName ||
                account.appName ||
                accountAlias ||
                accountName ||
                null;
            const clientName = clientCandidate ? String(clientCandidate).trim() : null;

            return {
                accountId,
                name: accountName,
                alias: accountAlias,
                client: clientName,
                balance: account.balance ?? account.capital ?? null,
                risk: account.risk ?? null,
                netPnl: account.netPnl ?? null,
                strategies: Array.isArray(account.strategies)
                    ? account.strategies.map((strategy) => ({
                          ...strategy,
                          layers: Array.isArray(strategy.layers) ? [...strategy.layers] : [],
                          events: Array.isArray(strategy.events) ? [...strategy.events] : [],
                          summary: { ...(strategy.summary || {}) },
                      }))
                    : [],
            };
        });
    }

    const summary = state.summary || {};
    const layers = Array.isArray(state.layers) ? state.layers : [];
    const events = Array.isArray(state.events) ? state.events : [];

    const strategyId = state.strategyId || summary.strategyId || defaultStrategyId;
    const strategyLabel = summary.strategyLabel || state.strategyName || state.name || defaultStrategyLabel || 'Strategy';
    const accountId = summary.accountId || `${strategyId}-primary`;
    const aliasCandidate = summary.accountAlias || summary.accountName || summary.client || null;
    const accountName = String(aliasCandidate || summary.accountName || 'Primary Account').trim();
    const accountAlias = aliasCandidate ? String(aliasCandidate).trim() : accountName;
    const rawClientName = summary.client ? String(summary.client).trim() : '';
    const clientName = rawClientName || accountAlias || accountName || 'Unassigned Client';

    return [
        {
            accountId,
            name: accountName,
            alias: accountAlias,
            client: clientName,
            balance: summary.balance ?? null,
            risk: summary.risk ?? null,
            strategies: [
                {
                    strategyId,
                    name: strategyLabel,
                    status: summary.status || 'initialising',
                    netPnl: summary.netPnl ?? 0,
                    summary: {
                        ...summary,
                        strategyId,
                        strategyLabel,
                        accountId,
                        accountName,
                        accountAlias,
                        client: clientName,
                    },
                    layers,
                    events,
                },
            ],
        },
    ];
};

const mergeMonitorStates = (pelicanState, bankniftyState, cobraState) => {
    const pelican = pelicanState || {};
    const banknifty = bankniftyState || {};
    const cobra = cobraState || {};

    const accountMap = new Map();
    const addAccounts = (accounts) => {
        if (!Array.isArray(accounts)) {
            return;
        }
        accounts.forEach((account) => {
            if (!account?.accountId) {
                return;
            }
            const clientName =
                account.client ||
                account.alias ||
                account.name ||
                account.accountId;
            if (!accountMap.has(account.accountId)) {
                accountMap.set(account.accountId, {
                    accountId: account.accountId,
                    name: account.name || `Account ${account.accountId}`,
                    alias: account.alias || account.name || account.accountId,
                    balance: account.balance ?? null,
                    risk: account.risk ?? null,
                    client: clientName,
                    strategies: [],
                });
            }
            const target = accountMap.get(account.accountId);
            if (account.name && !target.name) {
                target.name = account.name;
            }
            if (account.alias && !target.alias) {
                target.alias = account.alias;
            }
            if (account.balance != null) {
                target.balance = account.balance;
            }
            if (account.risk != null) {
                target.risk = account.risk;
            }
            if (!target.client && clientName) {
                target.client = clientName;
            }
            const incomingStrategies = Array.isArray(account.strategies) ? account.strategies : [];
            incomingStrategies.forEach((strategy) => {
                target.strategies.push({
                    ...strategy,
                    layers: Array.isArray(strategy.layers) ? [...strategy.layers] : [],
                    events: Array.isArray(strategy.events) ? [...strategy.events] : [],
                    summary: { ...(strategy.summary || {}) },
                });
            });
        });
    };

    addAccounts(
        composeMonitorAccounts(banknifty, {
            strategyId: 'banknifty8',
            strategyLabel: banknifty.summary?.strategyLabel || 'banknifty8',
            accountPrefix: 'banknifty-account',
        })
    );
    addAccounts(
        composeMonitorAccounts(cobra, {
            strategyId: 'cobra4',
            strategyLabel: cobra.summary?.strategyLabel || 'Cobra 4',
            accountPrefix: 'cobra-account',
        })
    );
    addAccounts(
        composeMonitorAccounts(pelican, {
            strategyId: 'pelican',
            strategyLabel: pelican.summary?.strategyLabel || 'Pelican',
            accountPrefix: 'pelican-account',
        })
    );

    const accounts = Array.from(accountMap.values()).map((account) => {
        const strategies = account.strategies.map((strategy) => {
            const netPnl = strategy.netPnl ?? strategy.summary?.netPnl ?? 0;
            const status = strategy.summary?.status || strategy.status || 'initialising';
            return {
                ...strategy,
                netPnl,
                status,
            };
        });

        const netPnl = strategies.reduce((total, strategy) => total + (Number(strategy.netPnl) || 0), 0);
        const liveLayers = strategies.reduce(
            (sum, strategy) =>
                sum +
                (strategy.layers?.filter((layer) => (layer.status || '').toLowerCase() === 'active').length || 0),
            0
        );
        const pendingLayers = strategies.reduce(
            (sum, strategy) =>
                sum +
                (strategy.layers?.filter((layer) =>
                    ['armed', 'pending'].includes((layer.status || '').toLowerCase())
                ).length || 0),
            0
        );

        return {
            accountId: account.accountId,
            name: account.name,
            alias: account.alias,
            client: account.client || account.alias || account.name || account.accountId,
            balance: account.balance ?? null,
            risk: account.risk ?? null,
            netPnl,
            status: deriveAccountStatus(strategies),
            metrics: {
                strategies: strategies.length,
                liveLayers,
                pendingLayers,
            },
            strategies,
        };
    });

    const layers = [
        ...(Array.isArray(pelican.layers) ? pelican.layers : []),
        ...(Array.isArray(banknifty.layers) ? banknifty.layers : []),
        ...(Array.isArray(cobra.layers) ? cobra.layers : []),
    ];

    const combinedEvents = [
        ...(Array.isArray(pelican.events) ? pelican.events : []),
        ...(Array.isArray(banknifty.events) ? banknifty.events : []),
        ...(Array.isArray(cobra.events) ? cobra.events : []),
    ]
        .sort((a, b) => {
            const aTime = a?.occurredAt ? new Date(a.occurredAt).getTime() : 0;
            const bTime = b?.occurredAt ? new Date(b.occurredAt).getTime() : 0;
            return bTime - aTime;
        })
        .slice(0, 100);

    const strategyStatuses = accounts
        .flatMap((account) => account.strategies || [])
        .map((strategy) => (strategy.status || '').toLowerCase());

    let status = 'initialising';
    if (strategyStatuses.some((value) => ['error', 'failed', 'halted'].includes(value))) {
        status = 'error';
    } else if (strategyStatuses.some((value) => ['warning', 'degraded'].includes(value))) {
        status = 'warning';
    } else if (strategyStatuses.some((value) => ['active', 'operational', 'connected', 'live'].includes(value))) {
        status = 'operational';
    } else if (strategyStatuses.every((value) => value === 'closed') && strategyStatuses.length) {
        status = 'closed';
    }

    const heartbeatCandidates = [
        pelican.summary?.lastHeartbeatAt,
        banknifty.summary?.lastHeartbeatAt,
        cobra.summary?.lastHeartbeatAt,
        ...layers.map((layer) => layer?.capturedAt).filter(Boolean),
        ...combinedEvents.map((event) => event?.occurredAt).filter(Boolean),
    ]
        .map((value) => {
            const date = value ? new Date(value) : null;
            return date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
        })
        .filter((value) => value != null);

    const lastHeartbeatAt = heartbeatCandidates.length
        ? new Date(Math.max(...heartbeatCandidates)).toISOString()
        : null;

    const lastMessage =
        combinedEvents.length > 0
            ? combinedEvents[0].message
            : pelican.summary?.lastMessage ||
              banknifty.summary?.lastMessage ||
              cobra.summary?.lastMessage ||
              null;

    const netPnl = accounts.reduce((total, account) => total + (Number(account.netPnl) || 0), 0);
    const liveLayers = layers.filter((layer) => (layer?.status || '').toLowerCase() === 'active').length;
    const simulationLayers = layers.filter((layer) => (layer?.status || '').toLowerCase() === 'simulation').length;

    return {
        strategyId: 'trading-cockpit',
        summary: {
            strategyId: 'trading-cockpit',
            strategyLabel: 'Trading Cockpit',
            status,
            netPnl,
            liveLayers,
            simulationLayers,
            lastHeartbeatAt,
            lastMessage,
        },
        layers,
        events: combinedEvents,
        accounts,
    };
};

const normaliseAccounts = (state) => {
    if (!state) {
        return [];
    }

    const rawAccounts = Array.isArray(state.accounts) ? state.accounts : null;
    if (rawAccounts?.length) {
        return rawAccounts.map((account, accountIndex) => {
            const accountId = account.accountId || account.id || `account-${accountIndex}`;
            const aliasCandidate =
                account.accountAlias || account.alias || account.accountName || account.name || account.label || null;
            const accountName = aliasCandidate || account.accountName || account.name || account.label || `Account ${accountIndex + 1}`;
            const accountAlias = aliasCandidate ? String(aliasCandidate).trim() : accountName;

            const strategies = Array.isArray(account.strategies)
                ? account.strategies.map((strategy, strategyIndex) =>
                      normalizeStrategy(strategy, {
                          strategyId: `${accountId}-strategy-${strategyIndex}`,
                      })
                  )
                : [];

            const initialStrategies =
                strategies.length > 0
                    ? strategies
                    : [
                          normalizeStrategy(
                              {
                                  strategyId: state.strategyId,
                                  name: state.strategyName,
                                  summary: state.summary,
                                  layers: state.layers,
                                  events: state.events,
                              },
                              {}
                          ),
                      ];

            const sanitisedStrategies = initialStrategies.map((strategy) => resetStrategyTelemetry(strategy));
            const enrichedStrategies = sanitisedStrategies.map((strategy) => ({
                ...strategy,
                accountId,
                accountName,
                accountAlias,
            }));

            const netPnl =
                account.netPnl ??
                enrichedStrategies.reduce((total, strategy) => total + (Number(strategy.netPnl) || 0), 0);
            const layers = enrichedStrategies.flatMap((strategy) => strategy.layers || []);

            return {
                accountId,
                name: accountName,
                alias: accountAlias,
                client:
                    account.client ||
                    account.alias ||
                    account.accountAlias ||
                    account.name ||
                    state.summary?.client ||
                    state.summary?.accountAlias ||
                    state.summary?.accountName ||
                    null,
                status: account.status || deriveAccountStatus(enrichedStrategies),
                netPnl,
                balance: account.balance ?? account.capital ?? null,
                risk: account.risk ?? null,
                metrics: {
                    strategies: enrichedStrategies.length,
                    liveLayers: layers.filter((layer) => (layer.status || '').toLowerCase() === 'active').length,
                    pendingLayers: layers.filter((layer) =>
                        ['armed', 'pending'].includes((layer.status || '').toLowerCase())
                    ).length,
                },
                strategies: enrichedStrategies,
            };
        });
    }

    const fallbackId = state.accountId || 'primary';
    const fallbackName = state.accountName || state.accountAlias || 'Primary Account';
    const fallbackAlias = state.accountAlias || state.broker || fallbackName;

    const fallbackStrategy = normalizeStrategy(
        {
            strategyId: state.strategyId || 'pelican',
            name: state.strategyName || 'Pelican',
            summary: state.summary || {},
            layers: state.layers || [],
            events: state.events || [],
        },
        {}
    );
    const sanitisedFallback = resetStrategyTelemetry(fallbackStrategy);
    const enrichedFallback = [
        {
            ...sanitisedFallback,
            accountId: fallbackId,
            accountName: fallbackName,
            accountAlias: fallbackAlias,
        },
    ];

    return [
        {
            accountId: fallbackId,
            name: fallbackName,
            alias: fallbackAlias || fallbackName,
            client:
                state.client ||
                state.accountAlias ||
                state.accountName ||
                sanitisedFallback.summary?.client ||
                sanitisedFallback.summary?.accountAlias ||
                sanitisedFallback.summary?.accountName ||
                null,
            status: sanitisedFallback.status,
            netPnl: enrichedFallback.reduce((total, strategy) => total + (Number(strategy.netPnl) || 0), 0),
            balance: state.balance ?? null,
            risk: state.risk ?? null,
            metrics: {
                strategies: enrichedFallback.length,
                liveLayers: enrichedFallback.reduce(
                    (count, strategy) =>
                        count +
                        (strategy.layers?.filter(
                            (layer) => (layer.status || '').toLowerCase() === 'active'
                        ).length || 0),
                    0
                ),
                pendingLayers: enrichedFallback.reduce(
                    (count, strategy) =>
                        count +
                        (strategy.layers?.filter((layer) =>
                            ['armed', 'pending'].includes((layer.status || '').toLowerCase())
                        ).length || 0),
                    0
                ),
            },
            strategies: enrichedFallback,
        },
    ];
};

const buildSummaryMetrics = (accounts, heartbeatAge) => {
    const strategies = accounts.flatMap((account) => account.strategies || []);
    const layers = strategies.flatMap((strategy) => strategy.layers || []);

    const totalNet = strategies.reduce((total, strategy) => total + (Number(strategy.netPnl) || 0), 0);
    const activeStrategies = strategies.filter((strategy) =>
        ['active', 'connected', 'operational', 'live'].includes((strategy.status || '').toLowerCase())
    ).length;
    const flaggedStrategies = strategies.filter((strategy) =>
        ['error', 'halted', 'failed'].includes((strategy.status || '').toLowerCase())
    ).length;
    const liveLayers = layers.filter((layer) => (layer.status || '').toLowerCase() === 'active').length;
    const armedLayers = layers.filter((layer) =>
        ['armed', 'pending'].includes((layer.status || '').toLowerCase())
    ).length;

    const heartbeatText = heartbeatAge == null ? '—' : `${heartbeatAge}s ago`;
    const heartbeatTone =
        heartbeatAge == null
            ? 'neutral'
            : heartbeatAge < 60
            ? 'positive'
            : heartbeatAge < 180
            ? 'warning'
            : 'negative';

    return {
        heartbeatLabel: heartbeatText,
        totals: {
            netPnl: totalNet,
            strategies: strategies.length,
            accounts: accounts.length,
            liveLayers,
            pendingLayers: armedLayers,
            activeStrategies,
            flaggedStrategies,
        },
    };
};

const buildStrategyMetrics = (account, strategy) => {
    if (!strategy) {
        return [];
    }

    const activeStrategies =
        account?.strategies?.filter((candidate) =>
            ['active', 'connected', 'operational', 'live'].includes((candidate.status || '').toLowerCase())
        ).length ?? 0;
    const liveLayers =
        strategy.layers?.filter((layer) => (layer.status || '').toLowerCase() === 'active').length ?? 0;
    const totalNet = Number(strategy.netPnl) || 0;

    let heartbeatAge = null;
    if (strategy.summary?.lastHeartbeatAt) {
        const timestamp = parseDateSafe(strategy.summary.lastHeartbeatAt);
        if (timestamp) {
            heartbeatAge = Math.max(0, Math.round((Date.now() - timestamp.getTime()) / 1000));
        }
    }
    const heartbeatText = heartbeatAge == null ? '—' : `${heartbeatAge}s ago`;
    const heartbeatTone =
        heartbeatAge == null
            ? 'neutral'
            : heartbeatAge < 60
            ? 'positive'
            : heartbeatAge < 180
            ? 'warning'
            : 'negative';

    return [
        {
            id: 'strategy-net-pnl',
            label: 'Total Net P&L',
            value: formatCurrency(totalNet),
            tone: pnlTone(totalNet),
            animateValue: totalNet,
            formatter: (val) => formatCurrency(val),
        },
        {
            id: 'strategy-active-count',
            label: 'Active Strategies',
            value: formatCount(activeStrategies),
            tone: activeStrategies > 0 ? 'positive' : 'neutral',
        },
        {
            id: 'strategy-live-layers',
            label: 'Live Layers',
            value: formatCount(liveLayers),
            tone: liveLayers > 0 ? 'positive' : 'neutral',
            animateValue: liveLayers,
            formatter: (val) => formatCount(Math.max(0, Math.round(val))),
        },
        {
            id: 'strategy-telemetry-age',
            label: 'Telemetry Age',
            value: heartbeatText,
            tone: heartbeatTone,
        },
    ];
};

const getInitials = (value) => {
    if (!value) {
        return '—';
    }
    const parts = String(value)
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase());
    return parts.join('') || value.slice(0, 2).toUpperCase();
};

const toneToChipColor = (tone) => {
    switch (tone) {
        case 'positive':
            return 'success';
        case 'negative':
            return 'error';
        case 'warning':
            return 'warning';
        default:
            return 'default';
    }
};

const toneToTypographyColor = (theme, tone) => {
    switch (tone) {
        case 'positive':
            return theme.palette.success.main;
        case 'negative':
            return theme.palette.error.main;
        case 'warning':
            return theme.palette.warning.main;
        default:
            return theme.palette.text.primary;
    }
};

const SEVERITY_FILTER_IDS = ['info', 'warn', 'error', 'fatal'];

const SEVERITY_OPTIONS = [
    { id: 'info', label: 'Info', color: 'info' },
    { id: 'warn', label: 'Warn', color: 'warning' },
    { id: 'error', label: 'Error', color: 'error' },
    { id: 'fatal', label: 'Fatal', color: 'error' },
];

const DEFAULT_SEVERITY_FILTER = new Set(SEVERITY_FILTER_IDS);

const normaliseSeverityForFilter = (severity) => {
    const value = (severity || 'info').toString().toLowerCase();
    if (value.includes('fatal')) {
        return 'fatal';
    }
    if (value.includes('error') || value.includes('fail')) {
        return 'error';
    }
    if (value.includes('warn')) {
        return 'warn';
    }
    return 'info';
};

const formatDuration = (seconds) => {
    if (seconds == null) {
        return '—';
    }
    const mins = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
};

const AnimatedNumber = ({ value, formatter = (val) => val }) => {
    const prefersReducedMotion = useReducedMotion();
    const numericValue = typeof value === 'number' ? value : Number(value) || 0;
    const spring = useSpring(numericValue, { stiffness: 180, damping: 24, mass: 0.8 });
    const [displayValue, setDisplayValue] = useState(numericValue);

    useEffect(() => {
        if (prefersReducedMotion) {
            setDisplayValue(numericValue);
            return;
        }
        spring.set(numericValue);
    }, [numericValue, prefersReducedMotion, spring]);

    useEffect(() => {
        if (prefersReducedMotion) {
            return;
        }
        const unsubscribe = spring.on('change', (latest) => setDisplayValue(latest));
        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [prefersReducedMotion, spring]);

    const formattedValue = formatter(displayValue);
    if (prefersReducedMotion) {
        return <span>{formatter(numericValue)}</span>;
    }
    return (
        <motion.span
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            animate={{ opacity: [0.85, 1], scale: [0.995, 1] }}
        >
            {formattedValue}
        </motion.span>
    );
};

const fetchSnapshotFromApi = async (signal) => {
    if (!API_BASE) {
        return null;
    }
    const response = await fetch(resolveApiUrl(SNAPSHOT_ENDPOINT), {
        headers: {
            Accept: 'application/json',
            ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
        },
        signal,
    });
    if (!response.ok) {
        throw new Error(`Snapshot request failed with status ${response.status}`);
    }
    return response.json();
};

const normaliseSnapshotPayload = (payload, fallback) => {
    if (!payload) {
        return fallback;
    }
    const state = payload.state || payload.mergedState || payload.telemetry || payload;
    if (!state || typeof state !== 'object') {
        return fallback;
    }
    const telemetryFreshness = payload.telemetryFreshness || payload.freshness || fallback.telemetryFreshness || [];
    return {
        mergedState: state,
        telemetryFreshness,
        source: 'api',
    };
};

const fetchActivityTelemetry = async (phase, signal) => {
    if (!API_BASE) {
        return null;
    }
    const origin =
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(resolveApiUrl(ACTIVITY_ENDPOINT), origin);
    url.searchParams.set('phase', phase);
    const response = await fetch(url.toString(), {
        headers: {
            Accept: 'application/json',
            ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
        },
        signal,
    });
    if (!response.ok) {
        throw new Error(`Activity request failed with status ${response.status}`);
    }
    return response.json();
};

const triggerActivityAction = async (phase, action) => {
    const response = await fetch(resolveApiUrl(ACTIVITY_ACTION_ENDPOINT), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
        },
        body: JSON.stringify({ phase, action }),
    });
    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Action ${action} failed`);
    }
    return response.json().catch(() => ({}));
};

const useTradingCockpitSnapshots = () => {
    const queryClient = useQueryClient();
    const {
        state: pelicanState,
        freshness: pelicanFreshness,
    } = usePelicanMonitor();
    const {
        state: bankniftyState,
        freshness: bankniftyFreshness,
    } = useBankniftyMonitor();
    const {
        state: cobraState,
        freshness: cobraFreshness,
    } = useCobraMonitor();

    const fallbackState = useMemo(
        () => mergeMonitorStates(pelicanState, bankniftyState, cobraState),
        [pelicanState, bankniftyState, cobraState]
    );

    const fallbackFreshness = useMemo(
        () => [
            { id: 'pelican', label: 'Pelican', ...(pelicanFreshness || {}) },
            { id: 'banknifty8', label: 'BankNifty 8', ...(bankniftyFreshness || {}) },
            { id: 'cobra4', label: 'Cobra 4', ...(cobraFreshness || {}) },
        ],
        [pelicanFreshness, bankniftyFreshness, cobraFreshness]
    );

    const fallbackSnapshot = useMemo(
        () => ({
            mergedState: fallbackState,
            telemetryFreshness: fallbackFreshness,
            source: 'fallback',
        }),
        [fallbackState, fallbackFreshness]
    );

    useEffect(() => {
        queryClient.setQueryData(SNAPSHOT_QUERY_KEY, (previous) => {
            if (previous && previous.source === 'api') {
                return previous;
            }
            return fallbackSnapshot;
        });
    }, [fallbackSnapshot, queryClient]);

    const apiEnabled = Boolean(API_BASE);
    const snapshotQuery = useQuery({
        queryKey: SNAPSHOT_QUERY_KEY,
        queryFn: async ({ signal }) => {
            const payload = await fetchSnapshotFromApi(signal);
            if (!payload) {
                throw new Error('snapshot-api-unavailable');
            }
            return normaliseSnapshotPayload(payload, fallbackSnapshot);
        },
        refetchInterval: 2000,
        staleTime: 1500,
        gcTime: 60_000,
        retry: 1,
        enabled: apiEnabled,
        placeholderData: fallbackSnapshot,
    });

    if (!apiEnabled) {
        return {
            data: fallbackSnapshot,
            isFetching: false,
            isLoading: false,
            isError: false,
        };
    }

    return {
        ...snapshotQuery,
        data: snapshotQuery.data ?? fallbackSnapshot,
    };
};

const normaliseActivityData = (payload, fallbackPulses) => {
    if (!payload) {
        return {
            pulses: fallbackPulses,
            events: [],
            summary: null,
            source: 'fallback',
        };
    }
    return {
        pulses: payload.pulses || fallbackPulses,
        events: payload.events || [],
        summary: payload.summary || null,
        source: 'api',
    };
};

const useActivityTelemetry = (phase, fallbackPulses) => {
    const enabled = Boolean(API_BASE);
    const fallback = useMemo(
        () => ({
            pulses: fallbackPulses,
            events: [],
            summary: null,
            source: 'fallback',
        }),
        [fallbackPulses]
    );
    const query = useQuery({
        queryKey: ['trading-cockpit', 'activities', phase],
        queryFn: async ({ signal }) => {
            const payload = await fetchActivityTelemetry(phase, signal);
            if (!payload) {
                throw new Error('activity-api-unavailable');
            }
            return normaliseActivityData(payload, fallbackPulses);
        },
        refetchInterval: 3000,
        staleTime: 2000,
        gcTime: 60_000,
        enabled,
        placeholderData: fallback,
    });

    if (!enabled) {
        return {
            data: fallback,
            isFetching: false,
            isLoading: false,
            isError: false,
        };
    }

    return {
        ...query,
        data: query.data ?? fallback,
    };
};

const StatusChip = ({ status, size = 'medium' }) => {
    const tone = statusTone(status);
    const chipColor = toneToChipColor(tone);
    const icon =
        tone === 'positive'
            ? <CheckCircleIcon fontSize="inherit" />
            : tone === 'negative'
            ? <ErrorOutlineIcon fontSize="inherit" />
            : tone === 'warning'
            ? <WarningAmberIcon fontSize="inherit" />
            : undefined;

    return (
        <Chip
            size={size}
            icon={icon}
            label={(status || 'unknown').toUpperCase()}
            color={chipColor === 'default' ? 'default' : chipColor}
            variant={chipColor === 'default' ? 'outlined' : 'filled'}
        />
    );
};

const MetricPill = ({ metric }) => {
    const theme = useTheme();
    const valueColor = toneToTypographyColor(theme, metric.tone);
    const isInteractive = Boolean(metric.onClick);
    const hasAnimatedValue =
        typeof metric.animateValue === 'number' && typeof metric.formatter === 'function';

    const handleKeyDown = (event) => {
        if (!isInteractive) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            metric.onClick(event);
        }
    };

    return (
        <GlassPaper
            component={isInteractive ? 'button' : 'div'}
            onClick={metric.onClick}
            onKeyDown={handleKeyDown}
            aria-label={metric.ariaLabel || metric.label}
            sx={{
                p: 1.25,
                minWidth: 140,
                flexGrow: 1,
                flexBasis: 140,
                border: '1px solid transparent',
                boxShadow: 'none',
                textAlign: 'left',
                cursor: isInteractive ? 'pointer' : 'default',
                '&:hover': { transform: isInteractive ? 'translateY(-2px)' : 'none' },
            }}
        >
            <Typography variant="caption" color="text.secondary">
                {metric.label}
            </Typography>
            <Typography variant="body1" sx={{ color: valueColor, fontWeight: 600 }}>
                {hasAnimatedValue ? (
                    <AnimatedNumber value={metric.animateValue} formatter={metric.formatter} />
                ) : (
                    metric.value
                )}
            </Typography>
            {metric.hint ? (
                <Typography variant="caption" color="text.secondary">
                    {metric.hint}
                </Typography>
            ) : null}
        </GlassPaper>
    );
};

const resolveActivityTone = (pulses) => {
    if (pulses.some((pulse) => pulse.status === 'error')) {
        return 'error';
    }
    if (pulses.some((pulse) => pulse.status === 'warning')) {
        return 'warning';
    }
    return 'success';
};

const ModeAwareTooltip = (props) => {
    const theme = useTheme();
    const surface =
        theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.92)' : 'rgba(15, 23, 42, 0.92)';
    return (
        <Tooltip
            arrow
            {...props}
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: surface,
                        color: 'common.white',
                        fontSize: 12,
                        px: 1.25,
                        py: 0.75,
                        boxShadow:
                            theme.palette.mode === 'dark'
                                ? '0 10px 30px rgba(0,0,0,0.45)'
                                : '0 10px 30px rgba(15,23,42,0.25)',
                    },
                },
                arrow: {
                    sx: {
                        color: surface,
                    },
                },
            }}
        />
    );
};

const ActivityTelemetryCard = ({
    title,
    pulses = [],
    events = [],
    severityFilter,
    onSeverityToggle,
    actions = [],
    onAction,
    pendingActionId,
}) => {
    const safePulses = Array.isArray(pulses) ? pulses : [];
    const tone = resolveActivityTone(safePulses);
    const chipColor = tone === 'error' ? 'error' : tone === 'warning' ? 'warning' : 'success';
    const chipLabel = tone === 'error' ? 'Error' : tone === 'warning' ? 'Warn' : 'OK';
    const latest = safePulses[0];
    const severitySet =
        severityFilter && severityFilter.size
            ? severityFilter
            : new Set(DEFAULT_SEVERITIES);

    return (
        <GlassPaper
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                p: 2,
                minHeight: 280,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">{title}</Typography>
                <Chip size="small" color={chipColor} label={chipLabel} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
                {latest
                    ? `${latest.message} · ${latest.timestamp ? formatRelativeTime(latest.timestamp) : 'just now'}`
                    : 'Awaiting first activity pulse.'}
            </Typography>
            <Divider />
            <Stack spacing={1}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Typography variant="subtitle2" sx={{ mr: 1 }}>
                        Activity Feed
                    </Typography>
                    <SeverityFilterChips value={severitySet} onToggle={onSeverityToggle} />
                </Stack>
                <CompactEventFeed events={events} severityFilter={severitySet} />
            </Stack>
            <Divider />
            <Stack spacing={1}>
                <Typography variant="subtitle2">Quick Actions</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {actions.map((action) => (
                        <Button
                            key={action.id}
                            variant="outlined"
                            size="small"
                            onClick={() => onAction?.(action)}
                            disabled={!onAction || pendingActionId === action.id}
                        >
                            {pendingActionId === action.id ? 'Running…' : action.label}
                        </Button>
                    ))}
                    {!actions.length ? (
                        <Typography variant="caption" color="text.secondary">
                            No actions available.
                        </Typography>
                    ) : null}
                </Stack>
            </Stack>
        </GlassPaper>
    );
};

const SeverityFilterChips = ({ value, onToggle }) => (
    <FormGroup row sx={{ gap: 0.5 }}>
        {SEVERITY_OPTIONS.map((option) => (
            <FormControlLabel
                key={option.id}
                control={
                    <Checkbox
                        size="small"
                        color={option.color}
                        checked={value.has(option.id)}
                        onChange={() => onToggle(option.id)}
                    />
                }
                sx={{ m: 0, mr: 0.75 }}
                componentsProps={{
                    typography: {
                        variant: 'caption',
                        sx: { fontWeight: 600, letterSpacing: 0.5 },
                    },
                }}
                label={option.label.toUpperCase()}
            />
        ))}
    </FormGroup>
);

const CompactEventFeed = ({ events, severityFilter }) => {
    const theme = useTheme();
    const allowed = severityFilter && severityFilter.size ? severityFilter : DEFAULT_SEVERITY_FILTER;

    const filtered = useMemo(() => {
        if (!Array.isArray(events)) {
            return [];
        }
        return [...events]
            .filter((event) => allowed.has(normaliseSeverityForFilter(event?.severity)))
            .sort((a, b) => {
                const timeA = new Date(a?.occurredAt || a?.time || 0).getTime();
                const timeB = new Date(b?.occurredAt || b?.time || 0).getTime();
                return timeB - timeA;
            });
    }, [events, allowed]);

    if (!filtered.length) {
        return (
            <GlassPaper
                sx={{
                    border: '1px dashed rgba(148, 163, 184, 0.35)',
                    boxShadow: 'none',
                    textAlign: 'center',
                    color: 'text.secondary',
                    '&:hover': { transform: 'none' },
                }}
            >
                No telemetry yet. Adjust severity filters to widen the feed.
            </GlassPaper>
        );
    }

    const shouldVirtualize = filtered.length > 30;

    const renderEvent = (event, index) => {
        const severity = normaliseSeverityForFilter(event?.severity);
        const chipColor =
            severity === 'fatal' ? 'error' : severity === 'error' ? 'error' : severity === 'warn' ? 'warning' : 'info';
        return (
            <GlassPaper
                key={event.id || `${severity}-${index}`}
                sx={{
                    px: 1.5,
                    py: 1,
                    borderLeft: `4px solid ${alpha(
                        theme.palette[chipColor === 'info' ? 'primary' : chipColor].main,
                        0.4
                    )}`,
                    boxShadow: 'none',
                    '&:hover': { transform: 'translateY(-2px)' },
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {event.message || event.summary || 'Event'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {event.scope || event.tags?.join(', ') || 'General'}
                        </Typography>
                    </Stack>
                    <Stack spacing={0.25} alignItems="flex-end">
                        <Chip size="small" label={severity.toUpperCase()} color={chipColor} variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(event.occurredAt || event.time)}
                        </Typography>
                    </Stack>
                </Stack>
            </GlassPaper>
        );
    };

    if (shouldVirtualize) {
        return (
            <Box sx={{ height: 240 }}>
                <Virtuoso data={filtered} itemContent={(index, event) => renderEvent(event, index)} />
            </Box>
        );
    }

    return (
        <Stack spacing={1}>
            {filtered.map((event, index) => renderEvent(event, index))}
        </Stack>
    );
};

const LayerList = ({ layers }) => {
    const theme = useTheme();

    if (!layers?.length) {
        return (
            <GlassPaper
                sx={{
                    border: '1px dashed rgba(148, 163, 184, 0.35)',
                    boxShadow: 'none',
                    textAlign: 'center',
                    color: 'text.secondary',
                    '&:hover': { transform: 'none' },
                }}
            >
                No layer telemetry yet.
            </GlassPaper>
        );
    }

    return (
        <Stack spacing={1} sx={{ maxHeight: 260, overflowY: 'auto', pr: 1 }}>
            {layers.map((layer, index) => {
                const pnlColor = toneToTypographyColor(theme, pnlTone(layer.netPnl));
                return (
                    <GlassPaper
                        key={layer.layerId || layer.id || index}
                        sx={{
                            p: 1.5,
                            boxShadow: 'none',
                            border: '1px solid transparent',
                            '&:hover': { transform: 'translateY(-2px)' },
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2">
                                    {layer.label || layer.layerLabel || layer.layerId || `Layer ${index + 1}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Snapshot {formatRelativeTime(layer.capturedAt)}
                                </Typography>
                            </Stack>
                            <StatusChip status={layer.status} size="small" />
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Net P&L
                                </Typography>
                                <Typography variant="body2" sx={{ color: pnlColor, fontWeight: 600 }}>
                                    {formatCurrency(layer.netPnl ?? 0)}
                                </Typography>
                            </Box>
                            {layer.openContracts != null || layer.contracts != null ? (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Contracts
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatCount(layer.openContracts ?? layer.contracts ?? 0)}
                                    </Typography>
                                </Box>
                            ) : null}
                            {layer.instrument || layer.symbol || layer.underlying ? (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Instrument
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {layer.instrument || layer.symbol || layer.underlying}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Stack>
                    </GlassPaper>
                );
            })}
        </Stack>
    );
};

const deriveStopOrders = (orders = []) =>
    orders.filter((order) => {
        const type = (order.type || '').toLowerCase();
        return type.includes('stop') || type.includes('sl') || Boolean(order.linkedStopId);
    });

const formatClockValue = (value) => {
    if (!value) {
        return '—';
    }
    if (typeof value === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
        const [hours, minutes] = value.split(':');
        return `${hours.padStart(2, '0')}:${minutes}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatExpiryLabel = (value) => {
    if (!value) {
        return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const SessionSummaryChips = ({ session, netPnl }) => {
    const entries = [
        { id: 'start', label: 'Start', value: formatClockValue(session?.start) },
        { id: 'stop', label: 'Stop', value: formatClockValue(session?.stop) },
        {
            id: 'closeUnderlying',
            label: 'Close Px',
            value:
                session?.closeUnderlying != null && session?.closeUnderlying !== ''
                    ? formatCurrency(session.closeUnderlying)
                    : null,
        },
    ].filter((entry) => entry.value && entry.value !== '—');

    const netTone = pnlTone(netPnl);
    const chipColor = toneToChipColor(netTone);

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
            {entries.map((entry) => (
                <Chip
                    key={entry.id}
                    size="small"
                    variant="outlined"
                    label={`${entry.label}: ${entry.value}`}
                    sx={{ textTransform: 'none' }}
                />
            ))}
            <Chip
                size="small"
                color={chipColor === 'default' ? 'default' : chipColor}
                label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
                            Net
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
                            <AnimatedNumber value={netPnl} formatter={(val) => formatCurrency(val)} />
                        </Typography>
                    </Stack>
                }
                sx={{ textTransform: 'none' }}
            />
        </Stack>
    );
};

const LegStatusGrid = ({ legs = [] }) => {
    const theme = useTheme();
    if (!legs.length) {
        return null;
    }
    return (
        <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
            {legs.map((leg) => {
                const tone = pnlTone(leg.pnl);
                const pnlColor = toneToTypographyColor(theme, tone);
                const title = leg.label || `${leg.optionType || ''} ${leg.strike || ''}`.trim() || 'Leg';
                return (
                    <Grid item xs={12} sm={6} md={4} key={leg.id || title}>
                        <GlassPaper
                            sx={{
                                p: 1.5,
                                boxShadow: 'none',
                                border: '1px solid transparent',
                                height: '100%',
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack spacing={0.25}>
                                    <Typography variant="subtitle2">{title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Exp {formatExpiryLabel(leg.expiry)}
                                    </Typography>
                                </Stack>
                                <StatusChip status={leg.status} size="small" />
                            </Stack>
                            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Sell
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatCurrency(leg.sellPrice)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        LTP
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatCurrency(leg.ltp)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        SL
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatCurrency(leg.stopTrigger)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        P&L
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: pnlColor, fontWeight: 600 }}>
                                        {formatCurrency(leg.pnl)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </GlassPaper>
                    </Grid>
                );
            })}
        </Grid>
    );
};

const buildRowDetail = (tabValue, row) => {
    if (tabValue === 'orders') {
        const priceValue = row.price ?? row.averagePrice ?? row.avgPrice;
        const price = priceValue != null ? formatCurrency(priceValue) : null;
        const time = row.time ? formatClockValue(row.time) : null;
        const stop = row.linkedStopId || row.stopOrderId || row.meta?.linkedStopId;
        return [
            price ? `Px ${price}` : null,
            time ? `@ ${time}` : null,
            stop ? `Linked ${stop}` : null,
        ]
            .filter(Boolean)
            .join(' · ');
    }
    if (tabValue === 'positions-open' || tabValue === 'positions-closed') {
        const avgValue = row.avgPrice ?? row.averagePrice;
        const mtmValue = row.mtm ?? row.mtmPnl ?? row.pnl;
        const avg = avgValue != null ? formatCurrency(avgValue) : null;
        const mtm = mtmValue != null ? formatCurrency(mtmValue) : null;
        return [avg ? `Avg ${avg}` : null, mtm ? `MTM ${mtm}` : null].filter(Boolean).join(' · ');
    }
    const triggerValue = row.triggerPrice ?? row.price;
    const trigger = triggerValue != null ? formatCurrency(triggerValue) : null;
    const linked = row.linkedStopId || row.parentOrderId || row.meta?.linkedOrderId;
    return [trigger ? `Trigger ${trigger}` : null, linked ? `Linked ${linked}` : null]
        .filter(Boolean)
        .join(' · ');
};

const VirtualizedTableBody = ({ rows, renderRow, height = 260, colSpan }) => {
    const shouldVirtualize = rows.length > 25;
    if (!shouldVirtualize) {
        return <TableBody>{rows.map((row, index) => renderRow(row, index))}</TableBody>;
    }
    return (
        <TableBody>
            <TableRow>
                <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }}>
                    <Box sx={{ height }}>
                        <Virtuoso data={rows} itemContent={(index, row) => renderRow(row, index)} />
                    </Box>
                </TableCell>
            </TableRow>
        </TableBody>
    );
};

const TRADE_VIEW_OPTIONS = [
    { value: 'orders', label: 'ORD', tooltip: 'Orders' },
    { value: 'positions-open', label: 'POS·O', tooltip: 'Positions · Open' },
    { value: 'positions-closed', label: 'POS·C', tooltip: 'Positions · Closed' },
    { value: 'stops', label: 'STP', tooltip: 'Stops' },
];

const StrategyTradesPanel = ({ strategy, activeTab, onTabChange, onRowSelect }) => {
    const orders = Array.isArray(strategy.orders) ? strategy.orders : [];
    const positions = Array.isArray(strategy.positions) ? strategy.positions : [];
    const openPositions = positions.filter((position) => (position.status || '').toLowerCase() === 'open');
    const closedPositions = positions.filter((position) => (position.status || '').toLowerCase() === 'closed');
    const stopOrders = deriveStopOrders(orders);
    const legSnapshots = Array.isArray(strategy.legs) ? strategy.legs : [];
    const session = strategy.session || strategy.summary?.session || {};
    const netPnlValue = strategy.summary?.netPnl ?? strategy.netPnl ?? 0;

    const tabValue = activeTab || 'orders';

    const tabRows = {
        orders,
        'positions-open': openPositions,
        'positions-closed': closedPositions,
        stops: stopOrders,
    };

    const activeRows = tabRows[tabValue] || [];
    const columns =
        tabValue === 'orders'
            ? ['Symbol / Time', 'Side', 'Qty', 'Price', 'Status', 'Type']
            : tabValue === 'positions-open' || tabValue === 'positions-closed'
            ? ['Symbol', 'Qty', 'Avg Price', 'MTM', 'Status']
            : ['Symbol', 'Qty', 'Type', 'Status', 'Linked'];

    const handleRowClick = (resourceType, row) => {
        if (typeof onRowSelect === 'function') {
            onRowSelect(resourceType, row);
        }
    };

    const renderSymbolCell = (row) => {
        const detail = buildRowDetail(tabValue, row);
        const detailLines = detail
            ? detail.split('·').map((entry) => entry.trim()).filter(Boolean)
            : [];
        const symbolLabel = row.symbol || row.name || row.type || '—';
        const content = (
            <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {symbolLabel}
                </Typography>
                {detail ? (
                    <Typography variant="caption" color="text.secondary">
                        {detail}
                    </Typography>
                ) : null}
            </Stack>
        );
        if (!detailLines.length) {
            return content;
        }
        return (
            <ModeAwareTooltip
                title={
                    <Stack spacing={0.25}>
                        {detailLines.map((line, idx) => (
                            <Typography key={idx} variant="caption">
                                {line}
                            </Typography>
                        ))}
                    </Stack>
                }
            >
                <Box component="span" sx={{ display: 'block' }}>
                    {content}
                </Box>
            </ModeAwareTooltip>
        );
    };

    const renderRow = (row, index) => {
        if (tabValue === 'orders') {
            return (
                <TableRow
                    key={row.id || index}
                    hover
                    onClick={() => handleRowClick('order', row)}
                    sx={{ cursor: onRowSelect ? 'pointer' : 'default' }}
                >
                    <TableCell>{renderSymbolCell(row)}</TableCell>
                    <TableCell>{row.side || '—'}</TableCell>
                    <TableCell align="right">{formatCount(row.qty ?? row.quantity ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.price ?? 0)}</TableCell>
                    <TableCell>
                        <Chip size="small" label={(row.status || 'pending').toUpperCase()} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.type || '—'}</TableCell>
                </TableRow>
            );
        }
        if (tabValue === 'positions-open' || tabValue === 'positions-closed') {
            return (
                <TableRow
                    key={row.id || index}
                    hover
                    onClick={() => handleRowClick('position', row)}
                    sx={{ cursor: onRowSelect ? 'pointer' : 'default' }}
                >
                    <TableCell>{renderSymbolCell(row)}</TableCell>
                    <TableCell align="right">{formatCount(row.qty ?? row.quantity ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.avgPrice ?? row.averagePrice ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.mtm ?? row.mtmPnl ?? 0)}</TableCell>
                    <TableCell>
                        <Chip size="small" label={(row.status || 'open').toUpperCase()} variant="outlined" />
                    </TableCell>
                </TableRow>
            );
        }
        return (
            <TableRow
                key={row.id || index}
                hover
                onClick={() => handleRowClick('stop', row)}
                sx={{ cursor: onRowSelect ? 'pointer' : 'default' }}
            >
                <TableCell>{renderSymbolCell(row)}</TableCell>
                <TableCell align="right">{formatCount(row.qty ?? row.quantity ?? 0)}</TableCell>
                <TableCell>{row.type || 'Stop'}</TableCell>
                <TableCell>
                    <Chip size="small" label={(row.status || 'pending').toUpperCase()} variant="outlined" />
                </TableCell>
                <TableCell>{row.linkedStopId || row.parentOrderId || '—'}</TableCell>
            </TableRow>
        );
    };

    return (
        <GlassPaper
            sx={{
                p: 2,
                boxShadow: 'none',
                border: '1px solid transparent',
            }}
        >
            <Stack spacing={1.5}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Activity
                    </Typography>
                    <ToggleButtonGroup
                        value={tabValue}
                        exclusive
                        onChange={onTabChange}
                        size="small"
                        color="primary"
                        sx={{ flexWrap: 'wrap' }}
                    >
                        {TRADE_VIEW_OPTIONS.map((option) => (
                            <ModeAwareTooltip key={option.value} title={option.tooltip}>
                                <ToggleButton value={option.value}>
                                    {option.label}{' '}
                                    {option.value === 'orders'
                                        ? `(${orders.length})`
                                        : option.value === 'positions-open'
                                        ? `(${openPositions.length})`
                                        : option.value === 'positions-closed'
                                        ? `(${closedPositions.length})`
                                        : `(${stopOrders.length})`}
                                </ToggleButton>
                            </ModeAwareTooltip>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
                <SessionSummaryChips session={session} netPnl={netPnlValue} />
                <LegStatusGrid legs={legSnapshots} />
            </Stack>
            {activeRows.length ? (
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column}>{column}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <VirtualizedTableBody rows={activeRows} renderRow={renderRow} colSpan={columns.length} />
                </Table>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No data for the selected tab.
                </Typography>
            )}
        </GlassPaper>
    );
};

const StrategyDetailSheet = ({ open, onClose, strategy }) => {
    const { slideOverState } = useTradingCockpitStore();
    const strategyEvents = strategy?.events || [];
    const strategyOrders = strategy?.orders || [];
    const strategyPositions = strategy?.positions || [];
    const { resourceType, resourceId, resource } = slideOverState || {};
    const dataset =
        resourceType === 'order'
            ? strategyOrders
            : resourceType === 'position'
            ? strategyPositions
            : resourceType === 'stop'
            ? deriveStopOrders(strategyOrders)
            : [];
    const resolvedResource =
        resource ||
        dataset.find(
            (entry) =>
                entry?.id === resourceId ||
                entry?.orderId === resourceId ||
                entry?.positionId === resourceId ||
                entry?.linkedStopId === resourceId ||
                entry?.time === resourceId
        ) ||
        null;

    const relatedEvents = useMemo(() => {
        if (!resourceId) {
            return strategyEvents;
        }
        const lowercaseId = String(resourceId).toLowerCase();
        return strategyEvents.filter((event) => {
            const tags = Array.isArray(event.tags) ? event.tags : [];
            const scope = event.scope ? [event.scope] : [];
            const searchSpace = [event.message, ...tags, ...scope]
                .filter(Boolean)
                .map((entry) => entry.toString().toLowerCase());
            return searchSpace.some((value) => value.includes(lowercaseId));
        });
    }, [resourceId, strategyEvents]);

    const title =
        resourceType === 'order'
            ? 'Order'
            : resourceType === 'position'
            ? 'Position'
            : resourceType === 'stop'
            ? 'Stop Order'
            : 'Detail';

    if (!open || !strategy) {
        return null;
    }

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box
                sx={{
                    width: { xs: '100vw', sm: 420 },
                    maxWidth: 480,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {strategy.displayName || strategy.strategyId}
                        </Typography>
                    </Box>
                    <Button size="small" onClick={onClose}>
                        Close
                    </Button>
                </Stack>

                <GlassPaper
                    sx={{
                        p: 2,
                        boxShadow: 'none',
                        border: '1px solid transparent',
                    }}
                >
                    {resolvedResource ? (
                        <Stack spacing={1}>
                            {Object.entries(resolvedResource).map(([key, value]) => (
                                <Stack key={key} direction="row" justifyContent="space-between" spacing={2}>
                                    <Typography variant="caption" color="text.secondary">
                                        {key}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Unable to locate details for this entry.
                        </Typography>
                    )}
                </GlassPaper>

                <Stack spacing={1}>
                    <Typography variant="subtitle2">Related Events</Typography>
                    <CompactEventFeed events={relatedEvents} severityFilter={null} />
                </Stack>
            </Box>
        </Drawer>
    );
};

const StrategyTelemetryCard = ({ strategy, account, highlight }) => {
    const theme = useTheme();
    const prefersReducedMotion = useReducedMotion();
    const {
        globalSeverity,
        perCardSeverity,
        updatePerCardSeverity,
        tabState,
        setTabState,
        setSlideOverState,
        slideOverState,
        resetSlideOverState,
    } = useTradingCockpitStore();
    const [layersAnchor, setLayersAnchor] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const severitySelection = perCardSeverity[strategy.strategyId] || globalSeverity;
    const resolvedSelection =
        Array.isArray(severitySelection) && severitySelection.length ? severitySelection : DEFAULT_SEVERITIES;
    const severitySet = useMemo(
        () => new Set(resolvedSelection.map((value) => value.toLowerCase())),
        [resolvedSelection]
    );
    const handleSeverityToggle = useCallback(
        (severityId) => {
            if (!SEVERITY_FILTER_IDS.includes(severityId)) {
                return;
            }
            const normalized = severityId.toUpperCase();
            const next = new Set(resolvedSelection);
            if (next.has(normalized)) {
                next.delete(normalized);
            } else {
                next.add(normalized);
            }
            const nextArray = next.size ? Array.from(next) : DEFAULT_SEVERITIES;
            updatePerCardSeverity(strategy.strategyId, nextArray);
        },
        [resolvedSelection, strategy.strategyId, updatePerCardSeverity]
    );

    const activeTab = tabState[strategy.strategyId] || 'orders';
    const handleTabChange = useCallback(
        (_event, value) => {
            if (value) {
                setTabState(strategy.strategyId, value);
            }
        },
        [strategy.strategyId, setTabState]
    );
    const handleRowSelect = useCallback(
        (resourceType, resource) => {
            if (!resource) {
                return;
            }
            const resourceId =
                resource?.id ||
                resource?.orderId ||
                resource?.positionId ||
                resource?.stopOrderId ||
                resource?.linkedStopId ||
                resource?.symbol ||
                resource?.time ||
                `${resourceType}-${Date.now()}`;
            setSlideOverState({
                strategyId: strategy.strategyId,
                resourceType,
                resourceId,
                resource,
            });
        },
        [setSlideOverState, strategy.strategyId]
    );
    const handleSlideOverClose = useCallback(() => {
        resetSlideOverState();
    }, [resetSlideOverState]);
    const slideOverOpen = slideOverState?.strategyId === strategy.strategyId;

    const telemetryTimestamp = latestTelemetryTimestamp(strategy);
    const telemetryAgeSeconds = useMemo(() => {
        if (!telemetryTimestamp) {
            return null;
        }
        return Math.max(0, Math.round((Date.now() - telemetryTimestamp.getTime()) / 1000));
    }, [telemetryTimestamp]);
    const telemetryTone =
        telemetryAgeSeconds == null
            ? 'neutral'
            : telemetryAgeSeconds < 10
            ? 'positive'
            : telemetryAgeSeconds < 30
            ? 'warning'
            : 'negative';
    const telemetryChipColor =
        telemetryTone === 'positive'
            ? 'success'
            : telemetryTone === 'warning'
            ? 'warning'
            : telemetryTone === 'negative'
            ? 'error'
            : 'default';
    const telemetryLabel = telemetryAgeSeconds == null ? 'No signal' : formatDuration(telemetryAgeSeconds);

    const accountLabel = account?.alias || account?.name || account?.accountId || 'Account';
    const strategyLabel = strategy.displayName || strategy.name || strategy.strategyId;
    const liveLayersCount =
        strategy.layers?.filter((layer) => (layer.status || '').toLowerCase() === 'active').length ?? 0;
    const totalPnL = strategy.summary?.totalPnL ?? strategy.netPnl ?? 0;

    const metricDefinitions = useMemo(
        () => [
            {
                id: 'total-pnl',
                label: 'Total P&L',
                value: formatCurrency(totalPnL),
                tone: pnlTone(totalPnL),
                animateValue: totalPnL,
                formatter: (val) => formatCurrency(val),
            },
            {
                id: 'live-layers',
                label: 'Live Layers',
                value: formatCount(liveLayersCount),
                tone: liveLayersCount > 0 ? 'positive' : 'neutral',
                hint: 'Click to inspect layers',
                onClick: (event) => setLayersAnchor(event.currentTarget),
                ariaLabel: `View live layers for ${strategyLabel}`,
                animateValue: liveLayersCount,
                formatter: (val) => formatCount(Math.max(0, Math.round(val))),
            },
            {
                id: 'telemetry-age',
                label: 'Telemetry Age',
                value: telemetryLabel,
                tone: telemetryTone,
                hint: 'Last heartbeat',
            },
        ],
        [liveLayersCount, strategyLabel, telemetryLabel, telemetryTone, totalPnL]
    );

    const emphasisStyles = highlight
        ? {
              borderColor: alpha(theme.palette.primary.main, 0.5),
              boxShadow:
                  theme.palette.mode === 'dark'
                      ? '0 24px 48px rgba(96, 165, 250, 0.35)'
                      : '0 24px 48px rgba(37, 99, 235, 0.22)',
          }
        : {};

    const handleCopyIdentifiers = useCallback(() => {
        const text = `${accountLabel} · ${strategyLabel}`;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => {});
        }
    }, [accountLabel, strategyLabel]);

    const menuOpen = Boolean(menuAnchor);
    const handleMenuOpen = (event) => setMenuAnchor(event.currentTarget);
    const handleMenuClose = () => setMenuAnchor(null);

    const layersOpen = Boolean(layersAnchor);
    const handleLayersClose = () => setLayersAnchor(null);
    const telemetryChipElement = (
        <Chip size="small" label={`Telemetry ${telemetryLabel}`} color={telemetryChipColor} variant="outlined" />
    );
    const shouldPulse = !prefersReducedMotion && telemetryAgeSeconds != null;

    return (
        <GlassPaper
            sx={{
                height: '100%',
                p: { xs: 2, md: 2.5 },
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                borderColor: 'transparent',
                ...emphasisStyles,
            }}
        >
            {/* Header row */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
            >
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <ModeAwareTooltip title={`${accountLabel} · ${strategyLabel}`}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                {accountLabel} · {strategyLabel}
                            </Typography>
                        </ModeAwareTooltip>
                        <ModeAwareTooltip title="Copy identifiers">
                            <IconButton size="small" onClick={handleCopyIdentifiers}>
                                <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                        </ModeAwareTooltip>
                        {highlight ? (
                            <Chip size="small" color="primary" icon={<BoltIcon fontSize="small" />} label="Focus" />
                        ) : null}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        Status updated {telemetryTimestamp ? formatRelativeTime(telemetryTimestamp) : '—'}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <StatusChip status={strategy.status} size="small" />
                    <IconButton onClick={handleMenuOpen}>
                        <MoreVertIcon />
                    </IconButton>
                </Stack>
            </Stack>

            {/* KPI row */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: { xs: 1.5, md: 2.5 },
                }}
            >
                {metricDefinitions.map((metric) => (
                    <MetricPill key={metric.id} metric={metric} />
                ))}
            </Box>

            {/* Tabular trades row */}
            <StrategyTradesPanel
                strategy={strategy}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onRowSelect={handleRowSelect}
            />

            {/* Event feed row */}
            <Stack spacing={1}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Event Feed
                    </Typography>
                    <SeverityFilterChips value={severitySet} onToggle={handleSeverityToggle} />
                </Stack>
                <CompactEventFeed events={strategy.events} severityFilter={severitySet} />
            </Stack>

            <Popover
                open={layersOpen}
                anchorEl={layersAnchor}
                onClose={handleLayersClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ width: 320, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Live Layers
                    </Typography>
                    <LayerList layers={strategy.layers} />
                </Box>
            </Popover>

            <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose}>
                <MenuItem disabled>
                    <ListItemIcon>
                        {(strategy.status || '').toLowerCase() === 'live' ? (
                            <PauseCircleOutlineIcon fontSize="small" />
                        ) : (
                            <PlayCircleOutlineIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <ListItemText primary="Pause/Resume Strategy" secondary="Coming soon" />
                </MenuItem>
                <MenuItem disabled>
                    <ListItemIcon>
                        <CancelPresentationIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Flatten Positions" secondary="Guarded action" />
                </MenuItem>
                <Divider />
                <MenuItem disabled>
                    <ListItemIcon>
                        <CloudDownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Download Orders CSV" />
                </MenuItem>
                <MenuItem disabled>
                    <ListItemIcon>
                        <CloudDownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Download Positions CSV" />
                </MenuItem>
                <MenuItem disabled>
                    <ListItemIcon>
                        <CloudDownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Download Events CSV" />
                </MenuItem>
                <Divider />
                <MenuItem disabled>
                    <ListItemIcon>
                        <OpenInNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Open focused view" />
                </MenuItem>
            </Menu>
            <StrategyDetailSheet
                open={slideOverOpen}
                onClose={handleSlideOverClose}
                strategy={strategy}
            />
        </GlassPaper>
    );
};

const TradingCockpitPage = () => {
    const { data: snapshotData } = useTradingCockpitSnapshots();
    const {
        status: catalogStatus,
        error: catalogError,
        accounts: catalogAccounts,
        lookups: catalogLookups,
    } = useTelemetryCatalog();

    const mergedState = snapshotData?.mergedState;
    const telemetryFreshness = snapshotData?.telemetryFreshness ?? [];

    const staleSources = useMemo(
        () => telemetryFreshness.filter((entry) => entry?.isStale),
        [telemetryFreshness]
    );

    const nextResetIso = useMemo(() => {
        const timestamps = telemetryFreshness
            .map((entry) => entry?.nextResetAt)
            .filter(Boolean)
            .map((value) => {
                const date = new Date(value);
                return Number.isNaN(date.getTime()) ? null : date.getTime();
            })
            .filter((time) => typeof time === 'number');
        if (!timestamps.length) {
            return null;
        }
        return new Date(Math.min(...timestamps)).toISOString();
    }, [telemetryFreshness]);

    const telemetryStatus = useMemo(() => {
        if (staleSources.length) {
            const summary = staleSources
                .map((entry) => {
                    const relative = entry.lastUpdatedAt ? formatRelativeTime(entry.lastUpdatedAt) : 'awaiting first sample';
                    return `${entry.label} (${relative})`;
                })
                .join(', ');
            return {
                severity: 'warning',
                message: `Telemetry awaiting new session data: ${summary}`,
            };
        }
        if (nextResetIso) {
            const resetDate = new Date(nextResetIso);
            if (!Number.isNaN(resetDate.getTime())) {
                const formatted = resetDate.toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                });
                return {
                    severity: 'info',
                    message: `Telemetry live · next reset ${formatted} IST`,
                };
            }
        }
        return null;
    }, [staleSources, nextResetIso]);

    const heartbeatAge = useMemo(() => {
        const timestamp = mergedState?.summary?.lastHeartbeatAt;
        if (!timestamp) {
            return null;
        }
        const updated = new Date(timestamp);
        if (Number.isNaN(updated.getTime())) {
            return null;
        }
        const diff = Date.now() - updated.getTime();
        return Math.max(0, Math.round(diff / 1000));
    }, [mergedState]);

    const telemetryAccounts = useMemo(() => normaliseAccounts(mergedState), [mergedState]);

    const telemetryStrategies = useMemo(
        () =>
            telemetryAccounts.flatMap((account) =>
                (account.strategies || []).map((strategy) => ({
                    ...strategy,
                    accountId: account.accountId,
                    accountName: account.name,
                    accountAlias: account.alias,
                }))
            ),
        [telemetryAccounts]
    );

    const summary = useMemo(() => buildSummaryMetrics(telemetryAccounts, heartbeatAge), [telemetryAccounts, heartbeatAge]);

    const aggregateAccount = useMemo(() => {
        const totalNet =
            summary.totals?.netPnl ??
            telemetryStrategies.reduce((total, strategy) => total + (Number(strategy.netPnl) || 0), 0);
        const liveLayers = summary.totals?.liveLayers ?? telemetryStrategies.reduce(
            (sum, strategy) =>
                sum + (strategy.layers?.filter((layer) => (layer.status || '').toLowerCase() === 'active').length || 0),
            0
        );
        const pendingLayers = summary.totals?.pendingLayers ?? telemetryStrategies.reduce(
            (sum, strategy) =>
                sum +
                (strategy.layers?.filter((layer) =>
                    ['armed', 'pending'].includes((layer.status || '').toLowerCase())
                ).length || 0),
            0
        );
        const status = deriveAccountStatus(telemetryStrategies);

        return {
            accountId: ALL_ACCOUNTS,
            name: 'All Accounts',
            alias: 'All Accounts',
            client: 'Portfolio',
            status,
            netPnl: totalNet,
            balance: null,
            risk: null,
            metrics: {
            strategies: summary.totals?.strategies ?? telemetryStrategies.length,
            liveLayers,
            pendingLayers,
        },
            strategies: telemetryStrategies,
        };
    }, [telemetryStrategies, summary]);

    const telemetryAccountsById = useMemo(() => {
        const map = new Map();
        telemetryAccounts.forEach((account) => {
            const key = normaliseAccountKey(account.accountId || account.id);
            if (key) {
                map.set(key, account);
            }
        });
        return map;
    }, [telemetryAccounts]);

    const telemetryStrategiesByAccount = useMemo(() => {
        const map = new Map();
        telemetryStrategies.forEach((strategy) => {
            const accountKey = normaliseAccountKey(strategy.accountId);
            const strategyKey = normaliseStrategyKey(strategy.strategyId || strategy.id);
            if (!accountKey || !strategyKey) {
                return;
            }
            if (!map.has(accountKey)) {
                map.set(accountKey, new Map());
            }
            if (!map.get(accountKey).has(strategyKey)) {
                map.get(accountKey).set(strategyKey, strategy);
            }
        });
        return map;
    }, [telemetryStrategies]);

    const accountPanels = useMemo(() => {
        const panelMap = new Map();
        const accountLookup = catalogLookups.accountLookup || new Map();
        const accountsToStrategies = catalogLookups.accountsToStrategies || new Map();
        const catalogAliasIndex = new Map();
        let primeKey = null;

        const registerAlias = (value, targetKey) => {
            const aliasKey = normaliseAccountKey(value);
            if (aliasKey && !catalogAliasIndex.has(aliasKey)) {
                catalogAliasIndex.set(aliasKey, targetKey);
            }
        };

        const resolveAccountKey = (accountId, alias, client) => {
            const direct = normaliseAccountKey(accountId);
            if (direct && catalogAliasIndex.has(direct)) {
                return catalogAliasIndex.get(direct);
            }
            const aliasKey = normaliseAccountKey(alias);
            if (aliasKey && catalogAliasIndex.has(aliasKey)) {
                return catalogAliasIndex.get(aliasKey);
            }
            const clientKey = normaliseAccountKey(client);
            if (clientKey && catalogAliasIndex.has(clientKey)) {
                return catalogAliasIndex.get(clientKey);
            }
            if (
                primeKey &&
                ['primary', 'primary account'].includes((accountId || '').trim().toLowerCase())
            ) {
                return primeKey;
            }
            if (
                primeKey &&
                ['primary', 'primary account'].includes((alias || '').trim().toLowerCase())
            ) {
                return primeKey;
            }
            return direct || aliasKey || clientKey;
        };

        const toNumber = (value) => {
            const num = Number(value);
            return Number.isNaN(num) ? 0 : num;
        };

        const ensureMetrics = (strategiesList) => {
            const liveLayers = strategiesList.reduce(
                (sum, entry) =>
                    sum +
                    (entry.telemetry?.layers?.filter((layer) => (layer.status || '').toLowerCase() === 'active').length || 0),
                0
            );
            const pendingLayers = strategiesList.reduce(
                (sum, entry) =>
                    sum +
                    (entry.telemetry?.layers?.filter((layer) =>
                        ['armed', 'pending'].includes((layer.status || '').toLowerCase())
                    ).length || 0),
                0
            );
            return {
                strategies: strategiesList.length,
                liveLayers,
                pendingLayers,
            };
        };

        const deriveStatusFromStrategies = (strategiesList) =>
            deriveAccountStatus(
                strategiesList.map((entry) => entry.telemetry || { status: entry.status || 'scheduled' })
            );

        const upsertPanel = (key, base) => {
            if (!key) {
                return;
            }
            const existing = panelMap.get(key);
            const catalogAccount = accountLookup.get(key) || existing?.catalogAccount || null;
            const telemetryAccount = telemetryAccountsById.get(key) || existing?.telemetry || null;
            const mergedStrategies = mergeStrategyEntries(existing?.strategies, base.scheduledStrategies || []);

            const metrics = telemetryAccount?.metrics || ensureMetrics(mergedStrategies);
            const netPnl =
                telemetryAccount?.netPnl ??
                mergedStrategies.reduce((total, entry) => total + toNumber(entry.netPnl), 0);
            const status = telemetryAccount?.status || deriveStatusFromStrategies(mergedStrategies);

            panelMap.set(key, {
                accountId: existing?.accountId || base.accountId || telemetryAccount?.accountId || key,
                sourceAccountId: telemetryAccount?.accountId || base.sourceAccountId || existing?.sourceAccountId || null,
                name:
                    catalogAccount?.accountName ||
                    telemetryAccount?.name ||
                    base.accountName ||
                    existing?.name ||
                    base.alias ||
                    base.accountId,
                alias:
                    catalogAccount?.alias ||
                    telemetryAccount?.alias ||
                    base.alias ||
                    existing?.alias ||
                    base.accountName ||
                    base.accountId,
                client:
                    telemetryAccount?.client ||
                    catalogAccount?.client ||
                    base.client ||
                    existing?.client ||
                    base.accountId,
                broker: catalogAccount?.broker || base.broker || telemetryAccount?.broker || existing?.broker || null,
                isPrime: Boolean(catalogAccount?.isPrime || base.isPrime || existing?.isPrime),
                status,
                netPnl,
                metrics,
                scheduledStrategies: mergedStrategies,
                telemetry: telemetryAccount || existing?.telemetry || null,
                strategies: mergedStrategies,
                catalogAccount,
            });
        };

        const buildScheduledEntry = (accountKey, entry) => {
            const strategyId = entry.strategyId || entry.strategy_name || entry.strategyName || entry.strategy;
            const strategyKey = normaliseStrategyKey(strategyId);
            const telemetryMatch = telemetryStrategiesByAccount.get(accountKey)?.get(strategyKey) || null;
            const summary = telemetryMatch?.summary || {};
            const layers = Array.isArray(telemetryMatch?.layers) ? telemetryMatch.layers : [];
            const events = Array.isArray(telemetryMatch?.events) ? telemetryMatch.events : [];
            const statusValue = telemetryMatch?.status || summary?.status || 'scheduled';
            const netPnlValue = toNumber(telemetryMatch?.netPnl ?? summary?.netPnl ?? 0);

            return {
                strategyId,
                displayName: entry.displayName || telemetryMatch?.summary?.strategyLabel || strategyId,
                category: entry.category,
                type: entry.type,
                activityType: entry.activityType,
                schedule: Array.isArray(entry.schedule) ? entry.schedule : [],
                isScheduled: entry.isScheduled,
                telemetry: telemetryMatch,
                status: statusValue,
                netPnl: netPnlValue,
                summary,
                layers,
                events,
            };
        };

        catalogAccounts.forEach((account) => {
            const rawId =
                account.accountId ||
                account.app_name ||
                account.appName ||
                account.alias ||
                account.account_name;
            const key = normaliseAccountKey(rawId);
            if (!key) {
                return;
            }

            const aliasCandidates = [
                rawId,
                account.accountId,
                account.account_id,
                account.accountName,
                account.account_name,
                account.alias,
                account.account_alias,
                account.client,
                account.client_name,
                account.app_name,
                account.appName,
            ];
            aliasCandidates.forEach((candidate) => registerAlias(candidate, key));
            if (!primeKey && account.isPrime) {
                primeKey = key;
            }

            const scheduledEntries = (accountsToStrategies.get(key) || []).map((entry) =>
                buildScheduledEntry(key, entry)
            );

            upsertPanel(key, {
                accountId: rawId,
                accountName: account.accountName || account.account_name || account.alias || rawId,
                alias: account.alias || account.accountAlias || account.account_name || rawId,
                broker: account.broker || null,
                isPrime: account.isPrime,
                client: account.client || account.clientName || account.accountName || rawId,
                scheduledStrategies: scheduledEntries,
            });
        });

        if (primeKey) {
            registerAlias('primary', primeKey);
            registerAlias('Primary Account', primeKey);
            registerAlias(`${primeKey}-primary`, primeKey);
        }

        telemetryAccountsById.forEach((telemetryAccount, key) => {
            const resolvedKey =
                resolveAccountKey(telemetryAccount.accountId, telemetryAccount.alias, telemetryAccount.client) ||
                key ||
                normaliseAccountKey(telemetryAccount.accountId) ||
                `telemetry-${panelMap.size}`;
            const strategiesForAccount = Array.from(
                telemetryStrategiesByAccount.get(key)?.values() || []
            ).map((strategy) => ({
                strategyId: strategy.strategyId,
                displayName: strategy.summary?.strategyLabel || strategy.strategyId,
                category: 'strategy',
                type: 'strategy',
                schedule: [],
                isScheduled: false,
                telemetry: strategy,
                status: strategy.status || strategy.summary?.status || 'live',
                netPnl: toNumber(strategy.netPnl ?? strategy.summary?.netPnl ?? 0),
                summary: strategy.summary || {},
                layers: Array.isArray(strategy.layers) ? strategy.layers : [],
                events: Array.isArray(strategy.events) ? strategy.events : [],
            }));

            upsertPanel(resolvedKey, {
                accountId: resolvedKey,
                sourceAccountId: telemetryAccount.accountId,
                accountName: telemetryAccount.name || telemetryAccount.accountName || telemetryAccount.alias,
                alias: telemetryAccount.alias || telemetryAccount.name || telemetryAccount.accountId,
                broker: telemetryAccount.broker || null,
                client: telemetryAccount.client || telemetryAccount.accountAlias || telemetryAccount.accountName,
                scheduledStrategies: strategiesForAccount,
            });
        });

        const panels = Array.from(panelMap.values())
            .map(({ catalogAccount, ...panel }) => panel)
            .filter((panel) => panel.accountId)
            .sort((a, b) => {
                if (a.isPrime !== b.isPrime) {
                    return a.isPrime ? -1 : 1;
                }
                return (a.alias || a.name || '').localeCompare(b.alias || b.name || '');
            });

        return panels;
    }, [
        catalogAccounts,
        catalogLookups,
        telemetryAccountsById,
        telemetryStrategiesByAccount,
    ]);

    const {
        selectedAccounts,
        selectedStrategies,
        globalSeverity,
        perCardSeverity,
        tabState,
        activitySeverity,
        slideOverState,
        setSelectedAccounts,
        setSelectedStrategies,
        setGlobalSeverity,
        setPerCardSeverityMap,
        setTabStateMap,
        setActivitySeverity,
        setActivitySeverityMap,
        setSlideOverState,
    } = useTradingCockpitStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialSearchRef = useRef(searchParams);
    const hasHydratedRef = useRef(false);
    const urlCacheRef = useRef('');

    useEffect(() => {
        const parseList = (value) =>
            value
                ? value
                      .split(',')
                      .map((entry) => entry.trim())
                      .filter(Boolean)
                : [];
        const readStoredList = (key) => {
            if (typeof window === 'undefined') {
                return [];
            }
            try {
                const raw = window.localStorage.getItem(key);
                if (!raw) {
                    return [];
                }
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed)
                    ? parsed.map((entry) => String(entry).trim()).filter(Boolean)
                    : [];
            } catch (_error) {
                return [];
            }
        };
        const readStoredObject = (key) => {
            if (typeof window === 'undefined') {
                return null;
            }
            try {
                const raw = window.localStorage.getItem(key);
                if (!raw) {
                    return null;
                }
                const parsed = JSON.parse(raw);
                return parsed && typeof parsed === 'object' ? parsed : null;
            } catch (_error) {
                return null;
            }
        };

        const initialParams = new URLSearchParams(initialSearchRef.current);
        const accountsFromQuery = parseList(initialParams.get('accounts'));
        const strategiesFromQuery = parseList(initialParams.get('strategies'));
        const severityFromQuery = parseList(initialParams.get('severity')).filter((value) =>
            DEFAULT_SEVERITIES.includes(value)
        );

        if (accountsFromQuery.length) {
            setSelectedAccounts(accountsFromQuery);
        } else {
            const storedAccounts = readStoredList('tc:selectedAccounts');
            if (storedAccounts.length) {
                setSelectedAccounts(storedAccounts);
            }
        }

        if (strategiesFromQuery.length) {
            setSelectedStrategies(strategiesFromQuery);
        } else {
            const storedStrategies = readStoredList('tc:selectedStrategies');
            if (storedStrategies.length) {
                setSelectedStrategies(storedStrategies);
            }
        }

        if (severityFromQuery.length) {
            setGlobalSeverity(severityFromQuery);
        } else {
            const storedSeverity = readStoredList('tc:globalSeverity').filter((value) =>
                DEFAULT_SEVERITIES.includes(value)
            );
            if (storedSeverity.length) {
                setGlobalSeverity(storedSeverity);
            }
        }

        const storedPerCard = readStoredObject('tc:perCardSeverity');
        if (storedPerCard) {
            setPerCardSeverityMap(storedPerCard);
        }

        const storedTabState = readStoredObject('tc:tabState');
        if (storedTabState) {
            setTabStateMap(storedTabState);
        }

        const storedActivitySeverity = readStoredObject('tc:activitySeverity');
        if (storedActivitySeverity) {
            setActivitySeverityMap(storedActivitySeverity);
        }

        const detailParam = initialParams.get('detail');
        if (detailParam) {
            const [detailStrategyId, detailResourceType, detailResourceId] = detailParam.split('|');
            if (detailStrategyId && detailResourceType && detailResourceId) {
                setSlideOverState({
                    strategyId: detailStrategyId,
                    resourceType: detailResourceType,
                    resourceId: detailResourceId,
                    resource: null,
                });
            }
        }

        hasHydratedRef.current = true;
        urlCacheRef.current = initialParams.toString();
    }, [
        setGlobalSeverity,
        setPerCardSeverityMap,
        setSelectedAccounts,
        setSelectedStrategies,
        setTabStateMap,
        setActivitySeverityMap,
        setSlideOverState,
    ]);

    useEffect(() => {
        if (!hasHydratedRef.current) {
            return;
        }
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('tc:selectedAccounts', JSON.stringify(selectedAccounts));
            window.localStorage.setItem('tc:selectedStrategies', JSON.stringify(selectedStrategies));
            window.localStorage.setItem('tc:globalSeverity', JSON.stringify(globalSeverity));
            window.localStorage.setItem('tc:perCardSeverity', JSON.stringify(perCardSeverity));
            window.localStorage.setItem('tc:tabState', JSON.stringify(tabState));
            window.localStorage.setItem('tc:activitySeverity', JSON.stringify(activitySeverity));
        }

        const params = new URLSearchParams();
        if (selectedAccounts.length) {
            params.set('accounts', selectedAccounts.join(','));
        }
        if (selectedStrategies.length) {
            params.set('strategies', selectedStrategies.join(','));
        }
        if (globalSeverity.length) {
            params.set('severity', globalSeverity.join(','));
        }
        if (
            slideOverState?.strategyId &&
            slideOverState?.resourceType &&
            slideOverState?.resourceId
        ) {
            params.set(
                'detail',
                [slideOverState.strategyId, slideOverState.resourceType, slideOverState.resourceId].join('|')
            );
        }

        const nextString = params.toString();
        if (nextString === urlCacheRef.current) {
            return;
        }
        urlCacheRef.current = nextString;
        setSearchParams(params, { replace: true });
    }, [
        globalSeverity,
        perCardSeverity,
        selectedAccounts,
        selectedStrategies,
        setSearchParams,
        tabState,
        activitySeverity,
        slideOverState,
    ]);

    const combinedStrategies = useMemo(
        () =>
            accountPanels.flatMap((account) =>
                (account.strategies || [])
                    .filter((strategy) => (strategy.type || 'strategy') === 'strategy')
                    .map((strategy) => ({
                        ...strategy,
                        accountId: account.accountId,
                        accountAlias: account.alias,
                        accountName: account.name,
                        accountClient: account.client,
                    }))
            ),
        [accountPanels]
    );

    const selectedAccountSet = useMemo(() => new Set(selectedAccounts || []), [selectedAccounts]);

    const filteredStrategies = useMemo(() => {
        if (!selectedAccountSet.size) {
            return combinedStrategies;
        }
        return combinedStrategies.filter((strategy) => selectedAccountSet.has(strategy.accountId));
    }, [combinedStrategies, selectedAccountSet]);

    const accountFilterOptions = useMemo(
        () =>
            accountPanels.map((account) => ({
                label: account.alias || account.name || account.accountId,
                value: account.accountId,
            })),
        [accountPanels]
    );

    const strategyFilterOptions = useMemo(() => {
        const map = new Map();
        filteredStrategies.forEach((strategy) => {
            const key = strategy.strategyId || strategy.displayName || strategy.name;
            if (!key) {
                return;
            }
            if (!map.has(key)) {
                map.set(key, {
                    label: strategy.displayName || strategy.name || key,
                    value: key,
                });
            }
        });
        return Array.from(map.values());
    }, [filteredStrategies]);

    useEffect(() => {
        if (!selectedStrategies?.length) {
            return;
        }
        const available = new Set(strategyFilterOptions.map((option) => option.value));
        const next = selectedStrategies.filter((value) => available.has(value));
        if (next.length !== selectedStrategies.length) {
            setSelectedStrategies(next);
        }
    }, [strategyFilterOptions, selectedStrategies, setSelectedStrategies]);

    const selectedAccountOptions = useMemo(
        () =>
            accountFilterOptions.filter((option) =>
                (selectedAccounts || []).includes(option.value)
            ),
        [accountFilterOptions, selectedAccounts]
    );

    const selectedStrategyOptions = useMemo(
        () =>
            strategyFilterOptions.filter((option) =>
                (selectedStrategies || []).includes(option.value)
            ),
        [strategyFilterOptions, selectedStrategies]
    );

    const handleAccountFilterChange = useCallback(
        (_event, nextValue) => {
            const ids = Array.isArray(nextValue) ? nextValue.map((option) => option.value) : [];
            setSelectedAccounts(ids);
        },
        [setSelectedAccounts]
    );

    const handleStrategyFilterChange = useCallback(
        (_event, nextValue) => {
            const ids = Array.isArray(nextValue) ? nextValue.map((option) => option.value) : [];
            setSelectedStrategies(ids);
        },
        [setSelectedStrategies]
    );

    const handleSeverityChange = useCallback(
        (_event, nextValue) => {
            const next = Array.isArray(nextValue) && nextValue.length ? nextValue : DEFAULT_SEVERITIES;
            setGlobalSeverity(next);
        },
        [setGlobalSeverity]
    );

    const activityActionsEnabled = Boolean(API_BASE);
    const handleActivityAction = useCallback(
        async (phase, actionId) => {
            if (!activityActionsEnabled) {
                return;
            }
            setActivityActionState({ phase, actionId });
            try {
                await triggerActivityAction(phase, actionId);
            } catch (error) {
                console.error(`Activity action ${actionId} failed`, error);
            } finally {
                setActivityActionState({ phase: null, actionId: null });
            }
        },
        [activityActionsEnabled]
    );

    const accountLookup = useMemo(() => {
        const map = new Map();
        accountPanels.forEach((account) => {
            map.set(account.accountId, account);
        });
        return map;
    }, [accountPanels]);

    const visibleStrategies = useMemo(() => {
        return filteredStrategies.filter((strategy) => {
            const matchesStrategy =
                !selectedStrategies?.length || selectedStrategies.includes(strategy.strategyId);
            return matchesStrategy;
        });
    }, [filteredStrategies, selectedStrategies]);

    const visibleStrategyCount = visibleStrategies.length;
    const headerTitle = 'Trading Cockpit';
    const headerSubtitle = useMemo(() => {
        const liveLayers = summary.totals?.liveLayers ?? 0;
        return `${formatCount(visibleStrategyCount)} strategies · ${formatCount(liveLayers)} live`;
    }, [visibleStrategyCount, summary]);

    const activityPulses = useMemo(
        () =>
            telemetryFreshness.map((entry) => ({
                id: entry.id,
                label: entry.label,
                status: entry.isStale ? 'warning' : 'success',
                timestamp: entry.lastUpdatedAt || entry.nextResetAt || null,
                message: entry.isStale ? 'Awaiting new session data' : 'Telemetry streaming',
            })),
        [telemetryFreshness]
    );

    const { preTradePulses, postTradePulses } = useMemo(() => {
        const pre = [];
        const post = [];
        activityPulses.forEach((pulse, index) => {
            if (index % 2 === 0) {
                pre.push(pulse);
            } else {
                post.push(pulse);
            }
        });
        return { preTradePulses: pre, postTradePulses: post };
    }, [activityPulses]);

    const preActivitySeverityValues =
        activitySeverity?.preTrade?.length ? activitySeverity.preTrade : globalSeverity;
    const postActivitySeverityValues =
        activitySeverity?.postTrade?.length ? activitySeverity.postTrade : globalSeverity;
    const preActivitySeverityKey = Array.isArray(preActivitySeverityValues)
        ? preActivitySeverityValues.join('|')
        : '';
    const postActivitySeverityKey = Array.isArray(postActivitySeverityValues)
        ? postActivitySeverityValues.join('|')
        : '';
    const toLowercaseSet = useCallback((values) => {
        const list = Array.isArray(values) && values.length ? values : DEFAULT_SEVERITIES;
        return new Set(list.map((value) => value.toLowerCase()));
    }, []);

    const preActivitySeveritySet = useMemo(
        () => toLowercaseSet(preActivitySeverityValues),
        [preActivitySeverityKey, toLowercaseSet]
    );
    const postActivitySeveritySet = useMemo(
        () => toLowercaseSet(postActivitySeverityValues),
        [postActivitySeverityKey, toLowercaseSet]
    );

    const handleActivitySeverityToggle = useCallback(
        (phaseKey, severityId) => {
            if (!SEVERITY_FILTER_IDS.includes(severityId)) {
                return;
            }
            const normalized = severityId.toUpperCase();
            const currentValues =
                phaseKey === 'preTrade'
                    ? preActivitySeverityValues
                    : postActivitySeverityValues;
            const next = new Set(currentValues);
            if (next.has(normalized)) {
                if (next.size === 1) {
                    return;
                }
                next.delete(normalized);
            } else {
                next.add(normalized);
            }
            setActivitySeverity(phaseKey, Array.from(next));
        },
        [postActivitySeverityValues, preActivitySeverityValues, setActivitySeverity]
    );

    const preTradeActivity = useActivityTelemetry('pre-trade', preTradePulses);
    const postTradeActivity = useActivityTelemetry('post-trade', postTradePulses);
    const preTradePulsesData = preTradeActivity.data?.pulses ?? preTradePulses;
    const postTradePulsesData = postTradeActivity.data?.pulses ?? postTradePulses;
    const preTradeEvents = preTradeActivity.data?.events ?? [];
    const postTradeEvents = postTradeActivity.data?.events ?? [];
    const [activityActionState, setActivityActionState] = useState({
        phase: null,
        actionId: null,
    });


    return (
        <Box
            sx={{
                minHeight: '70vh',
                bgcolor: 'background.default',
                color: 'text.primary',
                p: { xs: 2, md: 3 },
            }}
        >
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
                <GlassPaper
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: { xs: 1.5, md: 2 },
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h5">{headerTitle}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {headerSubtitle}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Last heartbeat&nbsp;
                            <strong>{summary.heartbeatLabel}</strong>
                        </Typography>
                    </Stack>
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                        flexWrap="wrap"
                    >
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={accountFilterOptions}
                            value={selectedAccountOptions}
                            onChange={handleAccountFilterChange}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => <TextField {...params} label="Accounts" size="small" />}
                            sx={{ minWidth: 220, flex: 1 }}
                        />
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={strategyFilterOptions}
                            value={selectedStrategyOptions}
                            onChange={handleStrategyFilterChange}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => <TextField {...params} label="Strategies" size="small" />}
                            sx={{ minWidth: 220, flex: 1 }}
                        />
                        <ToggleButtonGroup
                            value={globalSeverity}
                            onChange={handleSeverityChange}
                            aria-label="Severity filter"
                            size="small"
                            sx={{
                                borderRadius: 3,
                                flexWrap: 'wrap',
                            }}
                        >
                            {DEFAULT_SEVERITIES.map((severity) => (
                                <ToggleButton key={severity} value={severity} aria-label={severity}>
                                    {severity}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>
                </GlassPaper>

                {telemetryStatus ? (
                    <Alert severity={telemetryStatus.severity} sx={{ borderRadius: 2 }}>
                        {telemetryStatus.message}
                    </Alert>
                ) : null}

                {catalogStatus === 'error' ? (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        {catalogError?.message || 'Catalog data is unavailable; showing telemetry only.'}
                    </Alert>
                ) : null}

                <Grid container spacing={2}>
                    <Grid item xs={12} lg={8}>
                        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                            {visibleStrategies.length ? (
                                <Grid container spacing={2}>
                                    {visibleStrategies.map((strategy) => {
                                        const parentAccount = accountLookup.get(strategy.accountId) || aggregateAccount;
                                        return (
                                            <Grid item xs={12} md={6} key={strategy.strategyId}>
                                                <StrategyTelemetryCard strategy={strategy} account={parentAccount} />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ) : (
                                <Box
                                    sx={{
                                        borderRadius: 1,
                                        border: '1px dashed',
                                        borderColor: 'divider',
                                        p: 4,
                                        textAlign: 'center',
                                        color: 'text.secondary',
                                    }}
                                >
                                    <Typography variant="body1">No telemetry available at the moment.</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={2}>
                            <ActivityTelemetryCard
                                title="Pre-Trade"
                                pulses={preTradePulsesData}
                                events={preTradeEvents}
                                severityFilter={preActivitySeveritySet}
                                onSeverityToggle={(severityId) => handleActivitySeverityToggle('preTrade', severityId)}
                                actions={PRE_TRADE_ACTIONS}
                                onAction={
                                    activityActionsEnabled
                                        ? (action) => handleActivityAction('pre-trade', action.id)
                                        : undefined
                                }
                                pendingActionId={
                                    activityActionState.phase === 'pre-trade'
                                        ? activityActionState.actionId
                                        : null
                                }
                            />
                            <ActivityTelemetryCard
                                title="Post-Trade"
                                pulses={postTradePulsesData}
                                events={postTradeEvents}
                                severityFilter={postActivitySeveritySet}
                                onSeverityToggle={(severityId) => handleActivitySeverityToggle('postTrade', severityId)}
                                actions={POST_TRADE_ACTIONS}
                                onAction={
                                    activityActionsEnabled
                                        ? (action) => handleActivityAction('post-trade', action.id)
                                        : undefined
                                }
                                pendingActionId={
                                    activityActionState.phase === 'post-trade'
                                        ? activityActionState.actionId
                                        : null
                                }
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
};

export default TradingCockpitPage;
